<template>
  <div class="fi-login">
    <div v-if="!resetting_password" class="fi-card">
      <!-- brand head -->
      <div class="fi-head">
        <div class="fi-logo">
          <svg viewBox="-2 0 200 128" fill="none" role="img" aria-label="Fleet Integrations">
            <g stroke="#1976D2" stroke-width="8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M36 120 C18 120 4 106 4 88 C4 74 14 62 28 57 C26 42 36 30 52 30 C58 30 64 32 68 36 C76 18 92 8 110 8 C138 8 160 30 162 57 C180 60 192 76 192 92 C192 108 178 120 162 120 Z"/>
              <path d="M65 64 H133 M116 48 L133 64 L116 80"/>
              <path d="M133 92 H65 M82 76 L65 92 L82 108"/>
            </g>
          </svg>
        </div>
        <h1 class="fi-title">Fleet Integrations</h1>
        <p class="fi-subtitle">Sign in to your portal</p>
      </div>

      <!-- login form -->
      <form class="fi-form" @submit.prevent="login" @focusin="onFormActivity" @pointerdown="onFormActivity" @keydown="onFormActivity">
        <div class="fi-field">
          <label class="fi-label" for="username">Email</label>
          <div class="fi-input" :class="{ 'is-error': errored }">
            <svg class="fi-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>
            <input
              id="username"
              v-model="form.username"
              type="text"
              required
              autocomplete="username webauthn"
              autocapitalize="none"
              autocorrect="off"
              spellcheck="false"
              placeholder="you@company.com"
            />
          </div>
        </div>

        <div class="fi-field">
          <label class="fi-label" for="password">Password</label>
          <div class="fi-input" :class="{ 'is-error': errored }">
            <svg class="fi-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
            <input
              id="password"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              required
              autocomplete="current-password"
              placeholder="Enter your password"
              class="fs-exclude"
            />
            <button type="button" class="fi-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? 'Hide password' : 'Show password'">
              <svg v-if="!showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3l18 18"/><path d="M10.6 10.6a3 3 0 0 0 4.2 4.2"/><path d="M9.9 5.2A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a13.7 13.7 0 0 1-2.4 3.2M6.1 6.1A13.7 13.7 0 0 0 2 12s3.5 7 10 7a9.3 9.3 0 0 0 3-.5"/></svg>
            </button>
          </div>
        </div>

        <div class="fi-row-end">
          <b-link class="fi-link" @click="auth_navigation(true)">Forgot password?</b-link>
        </div>

        <transition name="fi-fade">
          <div v-if="alert.show && !errored && alert.variant === 'info'" class="fi-info">
            <svg class="fi-ic-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg>
            <span>{{ alert.message }}</span>
          </div>
        </transition>

        <transition name="fi-fade">
          <div v-if="errored || passkeyError" class="fi-error">
            <svg class="fi-ic-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>
            <span>{{ alert.message }}</span>
          </div>
        </transition>

        <button id="login-button" class="fi-submit" type="submit" :disabled="loading">
          <span v-if="loading" class="fi-spinner" aria-hidden="true"></span>
          <span>{{ loading ? 'Signing in&hellip;' : 'Sign in' }}</span>
        </button>

        <!-- FI-2182: only on a browser that has held a passkey before -->
        <div v-if="passkeyLink" class="fi-row-center">
          <button type="button" class="fi-link fi-passkey-link" :disabled="loading" @click="passkeySignIn('link')">Sign in with a passkey</button>
        </div>
      </form>

      <div class="fi-foot">
        <div class="fi-foot-links">
          <a class="fi-foot-link" target="_new" href="https://www.fleet-integrations.com">Learn more</a>
          <span class="fi-dot">&bull;</span>
          <a class="fi-foot-link" target="_new" href="mailto:support@initekconsulting.com">Contact us</a>
        </div>
        <a class="fi-powered" target="_new" href="https://www.initekconsulting.com/fleet-integrations">
          <span>powered by</span>
          <img height="18" src="/initek-logo.png" alt="Initek" />
        </a>
      </div>
    </div>

    <!-- forgot-password sub-view, kept in the same centered card -->
    <div v-else class="fi-card">
      <ForgotPassword @auth_navigation="auth_navigation" />
    </div>
  </div>
</template>

<script>
    import axios from 'axios'
    import {mapMutations} from "vuex";
    import ForgotPassword from './forgot_password'
    import { resetFavorites } from '@/nav/nav_favorites'
    import { isTokenExpired, homeRouteFor, isExplicitLoginVisit, SIGNOUT_KEY, SESSION_END_REASON_KEY } from '@/utils/session_renewal'
    import {
      passkeysEnabled, conditionalUiAvailable, getPasskey, passkeyErrorMessage, promptOnArrivalSupported,
      rememberPasskeyOnThisBrowser, hasPasskeyOnThisBrowser, notePasskeyRefused, passkeyRefusedOnThisBrowser,
      noteSignOut, quietAfterSignOut, clearSignOutQuiet, readPasskeyMarkers, restorePasskeyMarkers,
    } from '@/utils/passkeys'
    import { passkey_login_begin, passkey_login_complete } from '@/networking/apis'

    // a login challenge lives 5 minutes on the api. the autofill ceremony can
    // wait behind the field far longer than that, so it is re-minted before
    // the challenge it holds runs out: a first pick must not fail.
    const CHALLENGE_REFRESH_MS = 4 * 60 * 1000

    // sessionStorage is per tab and can be blocked outright; every touch is
    // guarded so the login page never fails to render over it
    const readSessionFlag = (key) => {
      try { return sessionStorage.getItem(key) } catch (_) { return null }
    }
    const dropSessionFlag = (key) => {
      try { sessionStorage.removeItem(key) } catch (_) { /* noop */ }
    }

    export default {
        name: 'login',
        components: { ForgotPassword },
        data() {
            return {
                loading: false,
                errored: false,
                form: { username:'', password:''},
                alert: { show:false, message:'', variant:''},
                resetting_password: false,
                showPassword: false,
                jwt: null,
                passkeyLink: false,
                // a refused or failed passkey sign-in: the message shows, the
                // untouched email and password fields stay as they are
                passkeyError: false,
            }
        },
        created(){
          // FI-2182: how many times a cancelled autofill prompt has been put
          // back this visit (see passkeySignIn)
          this._passkeyRestarts = 0
          // FI-2180: reaching the login page no longer ends a session that is
          // still good. an explicit sign-out (SIGNOUT_KEY) says the session is
          // over; otherwise a live jwt in storage means forward instead of
          // clearing. an api bounce writes SESSION_END_REASON_KEY and drops the
          // dead token (endSessionLocally), so a live jwt found next to a
          // leftover reason can only be a newer sign-in from another tab and
          // outranks it. the reason stays in sessionStorage until a sign-in
          // lands (writeSession) because the login page mounts twice on the way
          // in: once inside the authenticated shell's router-view, then again
          // in the signed-out branch after the first copy emits
          // authenticated=false.
          // FI-2268: opening #/login itself (typed, or a bookmark of the login
          // page) also ends the session, the way a shared workstation hands
          // over; only the '/' redirect and #/login/<page> forward.
          const signedOut = readSessionFlag(SIGNOUT_KEY)
          const endReason = readSessionFlag(SESSION_END_REASON_KEY)
          if (signedOut) {
            dropSessionFlag(SIGNOUT_KEY)
            dropSessionFlag(SESSION_END_REASON_KEY)
          }
          if (!signedOut && !isExplicitLoginVisit(this.$route) && this.forwardIfSignedIn()) {
            dropSessionFlag(SESSION_END_REASON_KEY)
            // FI-2182: the page is on its way out; no ceremony behind it
            this._passkeyForwarded = true
            return
          }

          // FI-2182: the per-browser passkey markers ride across the clear.
          // the hint is what earns the prompt on arrival and the fallback
          // link on a browser that has held a passkey before. they are put
          // back after the emit below: the shell answers authenticated=false
          // with a clear of its own (App.vue logout, which carries them too),
          // and the link must not depend on that.
          const passkeyMarkers = readPasskeyMarkers(localStorage)
          const passkeyHint = hasPasskeyOnThisBrowser(localStorage)
          localStorage.clear()
          this.passkeyLink = passkeyHint && passkeysEnabled(window)
          // localStorage.clear() cannot reach nav_favorites.js's in-memory
          // state, and logout is a router push rather than a reload -- so the
          // outgoing user's starred keys would otherwise carry into the next
          // sign-in on this tab. see resetFavorites().
          resetFavorites()
          this.$emit('authenticated', false)
          restorePasskeyMarkers(localStorage, passkeyMarkers)
          // no passkey prompt on arrival for a while after a sign-out (see
          // startAutoPasskey). in localStorage so every tab of this browser
          // sees it, including the ones the sign-out bounces next.
          if (signedOut) noteSignOut(localStorage)
          if (endReason === 'expired') {
            this.alert = { show: true, variant: 'info', message: 'Your session timed out. Sign in again and we will take you back to where you were.' }
          } else if (endReason === 'invalid') {
            this.alert = { show: true, variant: 'info', message: 'Your session ended. Sign in again and we will take you back to where you were.' }
          }
        },
        mounted() {
          // FI-2182: ask for the passkey on arrival where that makes sense;
          // otherwise (and after that prompt settles) the autofill ceremony
          // waits behind the username field
          if (this._passkeyForwarded) return
          if (!this.startAutoPasskey()) this.startConditionalPasskey()
        },
        beforeDestroy() {
          this._destroyed = true
          this.abortPasskey()
        },
        methods: {
          auth_navigation(bool){
            this.resetting_password = bool
          },
          // FI-2182: passkey sign-in, handled by the form itself. the username
          // field is marked for webauthn autofill and a conditional ceremony
          // waits behind it from mount: picking a passkey from the browser's
          // dropdown signs in through the same envelope as the password. a
          // browser that has held a passkey before also gets a fallback link
          // (the browser modal, which offers a phone by qr code as well). the
          // browser throws on a second navigator.credentials.get while one is
          // pending, so every start aborts the one before it and every await
          // checks that its ceremony is still the live one. the controller is
          // an instance field, not data: nothing renders off it.
          // safari anchors its autofill popup to the focused field. when the
          // page moves on with the email field still focused (a passkey
          // picked from its suggestions, which it then swaps for saved
          // passwords), the popup is left hanging over the next page. let go
          // of focus the moment a sign-in is under way: by beforeDestroy vue
          // has already taken the page out of the dom, too late for this.
          releaseFocus() {
            try {
              const el = typeof document !== 'undefined' ? document.activeElement : null
              if (el && this.$el && this.$el.contains(el) && typeof el.blur === 'function') el.blur()
            } catch (_) { /* cosmetic: never let it stop a sign-in */ }
          },
          abortPasskey() {
            const pending = this._passkeyAbort
            this._passkeyAbort = null
            this._passkeyActive = null
            if (pending) pending.abort()
            if (this._passkeyRefresh) {
              window.clearTimeout(this._passkeyRefresh)
              this._passkeyRefresh = null
            }
          },
          // nothing under cypress (no authenticator, and a pending get would
          // outlive the test) or on a stage with the flag off
          startConditionalPasskey(reuse) {
            if (window.Cypress || !passkeysEnabled(window)) return
            this.passkeySignIn('conditional', reuse)
          },
          // the prompt on arrival: on a browser that has held a passkey, a
          // fresh visit or a session that ended asks for it straight away
          // (the browser's own touch id or windows hello sheet), so an
          // expired session is one touch from where it was. not on webkit
          // (see promptOnArrivalSupported), where the email field's
          // suggestions are the way in. a browser that wants a click behind
          // a modal request refuses at once, which lands on the autofill
          // ceremony like any cancel. not for a while after
          // an explicit sign-out (whoever signs out and stays may be handing
          // the screen to someone else), and not after the api refused a
          // passkey from here (removed from the account, still on the
          // device) until one works again. true when a prompt was started.
          startAutoPasskey() {
            if (window.Cypress || !passkeysEnabled(window) || !this.passkeyLink) return false
            if (!promptOnArrivalSupported()) return false
            if (quietAfterSignOut(localStorage) || passkeyRefusedOnThisBrowser(localStorage)) return false
            this.passkeySignIn('auto')
            return true
          },
          // the autofill ceremony holds its challenge for as long as the page
          // sits; a fresh one replaces it before the api stops accepting it
          scheduleChallengeRefresh(mintedAt, live) {
            const wait = Math.max(1000, CHALLENGE_REFRESH_MS - (Date.now() - mintedAt))
            this._passkeyRefresh = window.setTimeout(() => {
              this._passkeyRefresh = null
              // a ceremony younger than half the window is not stale whatever
              // the timer says (a stubbed timer fires at once)
              if (live() && Date.now() - mintedAt >= CHALLENGE_REFRESH_MS / 2) this.startConditionalPasskey()
            }, wait)
          },
          // mode: 'conditional' for the autofill ceremony (silent, and put
          // back after a refused sign-in so the dropdown keeps working),
          // 'auto' for the prompt on arrival (silent too, apart from a
          // refused sign-in), 'link' for the fallback button, which speaks
          // for every failure. reuse: a challenge from a prompt that closed
          // before any authenticator saw it, handed to the autofill ceremony
          // so an arrival costs one begin call.
          async passkeySignIn(mode, reuse) {
            if (this._destroyed) return
            this.abortPasskey()
            const controller = new AbortController()
            this._passkeyAbort = controller
            const live = () => this._passkeyAbort === controller
            const conditional = mode === 'conditional'
            let stage = 'begin'
            let startedAt = 0
            let challenge = reuse || null
            // what onFormActivity reads: which ceremony is up, how far it got
            const active = { mode, stage, challenge }
            this._passkeyActive = active
            try {
              if (conditional && !(await conditionalUiAvailable(window))) return
              if (!live()) return
              if (!challenge) {
                const begin = await passkey_login_begin()
                challenge = { ...begin.data.data, mintedAt: Date.now() }
              }
              if (!live()) return
              stage = 'get'
              active.stage = stage
              active.challenge = challenge
              startedAt = Date.now()
              if (conditional) this.scheduleChallengeRefresh(challenge.mintedAt, live)
              const { options, challenge_token } = challenge
              const credential = await getPasskey(
                options, { mediation: conditional ? 'conditional' : undefined, signal: controller.signal }, window
              )
              if (!live()) return
              stage = 'complete'
              active.stage = stage
              this.releaseFocus()
              this.loading = true
              this.errored = false
              this.passkeyError = false
              const response = await passkey_login_complete({ challenge_token, credential })
              rememberPasskeyOnThisBrowser(localStorage)
              this.handle_login_response(response.data)
            } catch (e) {
              if (stage === 'complete') this.loading = false
              // null is the user closing the prompt, or an abort of our own.
              // the autofill ceremony and the prompt on arrival only speak for
              // a refused sign-in: a begin or get that fails on its own stays
              // quiet, the password path is right there. the link speaks for
              // all three.
              const message = passkeyErrorMessage(e, 'signin')
              if (message && (stage === 'complete' || mode === 'link')) {
                this.alert = { show: true, variant: 'danger', message }
                this.passkeyError = true
              }
              // the api refused a passkey this browser offered: most likely
              // one removed from the account that the device still holds. no
              // more prompts on arrival until a passkey works again here.
              if (stage === 'complete' && e && e.response && e.response.status === 401) notePasskeyRefused(localStorage)
              if (stage === 'begin') {
                // eslint-disable-next-line no-console
                console.log('passkey sign-in: begin failed', (e && e.message) || e)
                return
              }
              // an abort is ours (a password submit, a newer start or
              // beforeDestroy) and the caller decides what runs next
              if (e && e.name === 'AbortError') return
              if (stage === 'get' && conditional) {
                // the user picked a passkey from the dropdown and then closed
                // the touch id / windows hello prompt: put the ceremony back so
                // the dropdown keeps working. a few times at most, and only
                // after a ceremony that lived long enough to be interacted
                // with, so a browser that rejects conditional requests on its
                // own cannot loop begin calls. anything else stays down: the
                // password path is right there.
                const cancelled = e && e.name === 'NotAllowedError' && Date.now() - startedAt > 1000
                if (!cancelled || this._passkeyRestarts >= 3) return
                this._passkeyRestarts += 1
              }
              // a fresh conditional ceremony goes back behind the field. a
              // prompt that closed before any authenticator saw it (the sheet
              // dismissed, or refused outright) hands its challenge over:
              // nothing signed it, and the api burns a challenge at complete
              // only. a refused pick spent its challenge, so that one begins
              // again.
              const unspent = stage === 'get' && e && e.name === 'NotAllowedError' ? challenge : undefined
              this.startConditionalPasskey(unspent)
            } finally {
              if (live()) {
                this._passkeyAbort = null
                this._passkeyActive = null
              }
            }
          },
          // the prompt on arrival gives way the moment the user touches the
          // form. safari can take several seconds to raise its sheet, and it
          // must not land on top of someone already typing, or open while the
          // email field's own suggestions are showing. the dropdown carries on
          // with the same challenge (nothing signed it yet), so the passkey is
          // still one pick away. a sign-in already completing is left alone.
          onFormActivity() {
            const active = this._passkeyActive
            if (!active || active.mode !== 'auto' || active.stage === 'complete') return
            this.startConditionalPasskey(active.stage === 'get' ? active.challenge : undefined)
          },
          // FI-2180: a live session landed on the login page (bookmark, the
          // '/' redirect, a typed url). carry the stored user into the shell
          // the way App.vue created() does (picker selection re-applied) and
          // go where the user was headed, or to their usual landing page.
          // true when it forwarded; false leaves created() on today's path.
          forwardIfSignedIn() {
            let jwt = null
            let user = null
            let selected_tenant = null
            try {
              jwt = localStorage.getItem('jwt')
              if (!jwt || isTokenExpired(jwt, Date.now())) return false
              user = JSON.parse(localStorage.getItem('user') || 'null')
              selected_tenant = localStorage.getItem('selected_tenant')
            } catch (_) {
              return false
            }
            if (!user || typeof user !== 'object') return false
            if (selected_tenant != null) user.tenant = selected_tenant
            this.set_user_data(user)
            this.$emit('authenticated', true)
            const nav = this.$router.replace(this.forwardTarget(user))
            if (nav && nav.catch) nav.catch(() => {})
            return true
          },
          // the page the login route was asked to come back to, when it is a
          // real page; the login page itself is never a destination
          forwardTarget(user) {
            const nextUrl = this.$route && this.$route.params && this.$route.params.nextUrl
            if (nextUrl != null && nextUrl !== '') {
              const path = nextUrl.startsWith('/') ? nextUrl : '/' + nextUrl
              if (!path.startsWith('/login')) return path
            }
            return homeRouteFor(user)
          },
            async login() {
                if(this.form.username != "" && this.form.password != "") {
                    // FI-2182: the password takes over from the autofill
                    // ceremony; a miss puts a fresh one back behind the field
                    this.abortPasskey()
                    this.loading = true
                    this.errored = false
                    this.passkeyError = false
                    axios.post(
                        process.env.VUE_APP_API_URL + "bookings/auth/login",
                        this.form,
                        {headers: {'Access-Control-Allow-Origin': '*'}}
                    ).then((response) => {
                      this.handle_login_response(response.data);
                    }).catch(error => {
                      if (error.message && error.message == "Network Error")
                         this.alert = {show: true, message: "system in currently unavailable, try again later or contact support", variant: 'danger'}
                      else
                        this.alert = {show: true, message: "unable to login, check username & password or contact support", variant: 'danger'}
                      this.errored = true
                      this.loading = false
                      this.startConditionalPasskey()
                    });
                }
            },
            // the login envelope's data ({token, user}) becomes the session:
            // storage keys, then the vuex profile (user, theme, favorites bar).
            // shared by password login today and passkey login later (FI-2180).
            writeSession(data) {
                localStorage.setItem('user', JSON.stringify(data.user))
                localStorage.setItem('jwt', data.token)
                localStorage.setItem('authenticated', true)

                const jwt_data = this.h_get_jwt_data(data.token);
                localStorage.setItem('expiration_token_time', jwt_data.exp);
                // a sign-in landed: the "session timed out" reason has been read,
                // and the next arrival may ask for a passkey again
                dropSessionFlag(SESSION_END_REASON_KEY)
                clearSignOutQuiet(localStorage)

                this.set_user_data(data.user)
                // FI-1871: apply the user's saved site theme on login.
                this.set_site_theme(data.user.preferences && data.user.preferences.site_theme)
                // favorites bar under the horizon top nav.
                this.set_nav_favorites_bar(data.user.preferences && data.user.preferences.nav_favorites_bar)
            },
            async handle_login_response(data) {
                this.releaseFocus()
                setTimeout(() => {
                    this.writeSession(data.data)
                    setTimeout(() => {
                        this.$emit('authenticated', true)
                        if (this.$route.params.nextUrl != null) {
                            var nextUrl = this.$route.params.nextUrl
                            this.$router.push(nextUrl.startsWith('/') ? nextUrl : '/' + nextUrl)
                        } else if (data.data.user.role === 'driver' &&
                                   data.data.user.tenant === 'dattco-413f464d') {
                          this.$router.push({ name: 'driver_bid_board_tbn' })
                        } else if (data.data.user.home_page) {
                          this.$router.push(data.data.user.home_page)
                        } else if (data.data.user.role === 'booking-customer') {
                          // Booking-customer users should always land on booking portal list.
                          this.$router.push('/booking-customer')
                        } else {
                            this.$router.push('dashboard')
                        }
                        this.loading = false
                    }, 500)
                }, 500)
            },
           ...mapMutations('profile', [ 'set_user_data', 'set_site_theme', 'set_nav_favorites_bar' ]),
        }
    }
</script>

<style scoped>
/* fresh login, drawn from the mobile apps' design language */
.fi-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #0a0d14;
  background:
    radial-gradient(900px 500px at 50% -8%, rgba(25,118,210,0.12), transparent 60%),
    radial-gradient(700px 500px at 100% 110%, rgba(25,118,210,0.07), transparent 55%),
    linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%);
}

.fi-card {
  width: 100%;
  max-width: 408px;
  background: #fff;
  border: 1px solid rgba(16,24,40,0.06);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(16,24,40,0.10), 0 2px 6px rgba(16,24,40,0.04);
  padding: 40px 36px 28px;
  box-sizing: border-box;
}

/* head */
.fi-head { text-align: center; margin-bottom: 26px; }
.fi-logo { width: 66px; margin: 0 auto 16px; }
.fi-logo svg { width: 100%; height: auto; display: block; }
.fi-title {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #1976D2;
}
.fi-subtitle { margin: 4px 0 0; font-size: 14.5px; color: #5b6470; }

/* form */
.fi-form { display: flex; flex-direction: column; gap: 16px; }
.fi-field { display: flex; flex-direction: column; gap: 7px; }
.fi-label { font-size: 13px; font-weight: 600; color: #3a4250; text-align: left; }

.fi-input {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 14px;
  background: #f5f7fa;
  border: 1.5px solid #e3e8ef;
  border-radius: 11px;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.fi-input:focus-within {
  background: #fff;
  border-color: #1976D2;
  box-shadow: 0 0 0 4px rgba(25,118,210,0.12);
}
.fi-input.is-error {
  border-color: #e0796f;
  box-shadow: 0 0 0 4px rgba(192,57,43,0.10);
}
.fi-input .fi-ic { width: 18px; height: 18px; color: #98a2b3; flex: none; transition: color .15s ease; }
.fi-input:focus-within .fi-ic { color: #1976D2; }
.fi-input input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  /* 16px minimum: ios safari auto-zooms focused inputs below 16px */
  font-size: 16px;
  color: #0a0d14;
}
.fi-input input::placeholder { color: #a4abb8; }

.fi-eye {
  flex: none;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  margin-right: -6px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}
.fi-eye:hover { color: #5b6470; }
.fi-eye svg { width: 19px; height: 19px; }

.fi-row-end { display: flex; justify-content: flex-end; margin-top: -4px; }

.fi-link {
  color: #1976D2;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}
.fi-link:hover { text-decoration: underline; color: #1565C0; }

/* FI-2182: the fallback passkey link under the submit. a native button so it
   is keyboard reachable, dressed as the link above */
.fi-row-center { display: flex; justify-content: center; }
.fi-passkey-link { background: none; border: 0; padding: 0; margin-top: 2px; font-family: inherit; }
.fi-passkey-link:disabled { opacity: 0.5; cursor: default; text-decoration: none; }

.fi-error {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 12px;
  background: #fdecea;
  color: #b5372a;
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.35;
}
.fi-error .fi-ic-sm { width: 18px; height: 18px; flex: none; }

/* a quiet note (session timed out), same shape as the error box in the brand blue */
.fi-info {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 12px;
  background: #e8f1fb;
  color: #1c4f86;
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.35;
}
.fi-info .fi-ic-sm { width: 18px; height: 18px; flex: none; }

.fi-submit {
  position: relative;
  height: 48px;
  margin-top: 4px;
  border: 0;
  border-radius: 11px;
  background: linear-gradient(180deg, #1f86e6 0%, #1565C0 100%);
  color: #fff;
  font-size: 15.5px;
  font-weight: 650;
  letter-spacing: 0.01em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 8px 18px rgba(25,118,210,0.28);
  transition: filter .15s ease, transform .04s ease, box-shadow .15s ease;
}
.fi-submit:hover:not(:disabled) { filter: brightness(1.05); }
.fi-submit:active:not(:disabled) { transform: translateY(1px); }
.fi-submit:disabled { opacity: 0.65; cursor: default; box-shadow: none; }

.fi-spinner {
  width: 17px;
  height: 17px;
  border: 2.5px solid rgba(255,255,255,0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: fi-spin 0.7s linear infinite;
}
@keyframes fi-spin { to { transform: rotate(360deg); } }

.fi-foot {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #eef1f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.fi-foot-links { display: flex; align-items: center; gap: 10px; }
.fi-foot-link { color: #5b6470; font-size: 13px; font-weight: 500; text-decoration: none; }
.fi-foot-link:hover { color: #1976D2; }
.fi-dot { color: #c2c9d4; font-size: 12px; }
.fi-powered {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #98a2b3;
  text-decoration: none;
}
.fi-powered img { opacity: 0.9; }

/* error fade */
.fi-fade-enter-active, .fi-fade-leave-active { transition: opacity .2s ease; }
.fi-fade-enter, .fi-fade-leave-to { opacity: 0; }

@media (max-width: 440px) {
  .fi-card { padding: 32px 22px 24px; border-radius: 16px; }
}
</style>
